import { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../../firebase';

const TYPE_LABELS = {
  new_request:        'New Request',
  status_updated:     'Status Update',
  supplier_confirmed: 'Booking Confirmation',
  request_amended:    'Amendment Alert',
  new_message:        'New Message',
  invoice_issued:     'Invoice',
  quote_approval_request: 'Quote Approval',
  quote_needs_revision:   'Quote Revision',
};

const TYPE_COLOURS = {
  new_request:        'bg-blue-light text-pb',
  status_updated:     'bg-[#F5F3FF] text-[#5B21B6]',
  supplier_confirmed: 'bg-green-light text-[#065F46]',
  request_amended:    'bg-amber-light text-[#92400E]',
  new_message:        'bg-[#F3F4F6] text-[#374151]',
  invoice_issued:     'bg-amber-light text-[#92400E]',
  quote_approval_request: 'bg-blue-light text-pb',
  quote_needs_revision:   'bg-amber-light text-[#92400E]',
};

function EmailPreviewModal({ log, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[12px] shadow-2xl w-full max-w-2xl mx-4 flex flex-col overflow-hidden"
        style={{ maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#E5E7EB]">
          <div className="min-w-0">
            <p className="text-[11px] text-[#9CA3AF] mb-0.5">
              {TYPE_LABELS[log.type] || log.type} &middot; {formatDate(log.sentAt)}
            </p>
            <p className="text-[14px] font-semibold text-[#111827] truncate">{log.subject}</p>
            <p className="text-[12px] text-[#6B7280] mt-0.5">To: {(log.to || []).join(', ')}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-[#9CA3AF] hover:text-[#6B7280] text-lg leading-none mt-0.5 transition"
          >
            ✕
          </button>
        </div>

        {/* Attachment */}
        {log.attachmentUrl && (
          <div className="px-6 py-3 border-b border-[#E5E7EB] bg-surface flex items-center justify-between">
            <span className="text-[12.5px] text-[#374151]">📎 {log.attachmentName || 'Attachment'}</span>
            <a href={log.attachmentUrl} target="_blank" rel="noopener noreferrer"
              className="text-[12px] font-semibold text-pb hover:text-navy transition">
              View / download
            </a>
          </div>
        )}

        {/* Email body */}
        <div className="flex-1 overflow-auto">
          {log.html ? (
            <iframe
              srcDoc={log.html}
              title="Email preview"
              className="w-full border-0"
              style={{ minHeight: '500px' }}
              sandbox="allow-same-origin"
            />
          ) : (
            <div className="p-8 text-center text-[#9CA3AF] text-[13px]">
              No preview available for this email — it was sent before previews were enabled.
            </div>
          )}
        </div>

        {/* Footer */}
        {log.status === 'failed' && log.error && (
          <div className="px-6 py-3 bg-red-light border-t border-dr/20">
            <p className="text-[12px] font-medium text-dr mb-0.5">Send error</p>
            <p className="text-[12px] text-dr/80 font-mono break-all">{log.error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function EmailLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'emailLogs'), orderBy('sentAt', 'desc'), limit(200));
    const unsub = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const filtered = filter === 'all' ? logs : logs.filter((l) => l.status === filter);
  const sentCount   = logs.filter((l) => l.status === 'sent').length;
  const failedCount = logs.filter((l) => l.status === 'failed').length;

  return (
    <>
      {preview && (
        <EmailPreviewModal log={preview} onClose={() => setPreview(null)} />
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-[20px] font-bold text-[#111827]">Email Logs</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">Last 200 emails sent by the portal</p>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'sent', 'failed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-[12px] py-[6px] rounded-[8px] text-[12px] font-semibold capitalize transition-colors ${
                filter === f
                  ? 'bg-pb text-white'
                  : 'bg-white border border-[#E5E7EB] text-[#374151] hover:bg-surface'
              }`}
            >
              {f === 'all' ? `All (${logs.length})` : f === 'sent' ? `Sent (${sentCount})` : `Failed (${failedCount})`}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-[10px] border border-[#E5E7EB] px-[18px] py-[16px]">
          <p className="text-[12px] text-[#6B7280]">Total Sent</p>
          <p className="text-[26px] font-bold text-[#111827] mt-1">{sentCount}</p>
        </div>
        <div className="bg-white rounded-[10px] border border-[#E5E7EB] px-[18px] py-[16px]">
          <p className="text-[12px] text-[#6B7280]">Failed</p>
          <p className={`text-[26px] font-bold mt-1 ${failedCount > 0 ? 'text-dr' : 'text-[#111827]'}`}>{failedCount}</p>
        </div>
        <div className="bg-white rounded-[10px] border border-[#E5E7EB] px-[18px] py-[16px]">
          <p className="text-[12px] text-[#6B7280]">Total</p>
          <p className="text-[26px] font-bold text-[#111827] mt-1">{logs.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#9CA3AF] text-[13px]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[#9CA3AF] text-[13px]">No emails found.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-[18px] py-[10px] text-[11px] font-bold text-[#6B7280] uppercase">Date &amp; Time</th>
                <th className="text-left px-[18px] py-[10px] text-[11px] font-bold text-[#6B7280] uppercase">Type</th>
                <th className="text-left px-[18px] py-[10px] text-[11px] font-bold text-[#6B7280] uppercase">Recipient(s)</th>
                <th className="text-left px-[18px] py-[10px] text-[11px] font-bold text-[#6B7280] uppercase">Subject</th>
                <th className="text-left px-[18px] py-[10px] text-[11px] font-bold text-[#6B7280] uppercase">Status</th>
                <th className="px-[18px] py-[10px]" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((log) => (
                <tr key={log.id} className="border-b border-[#F3F4F6] hover:bg-surface transition-colors">
                  <td className="px-[18px] py-[10px] text-[#6B7280] text-[12px] whitespace-nowrap">{formatDate(log.sentAt)}</td>
                  <td className="px-[18px] py-[10px]">
                    <span className={`text-[11px] font-semibold px-[7px] py-[2px] rounded-[5px] ${TYPE_COLOURS[log.type] || 'bg-[#F3F4F6] text-[#374151]'}`}>
                      {TYPE_LABELS[log.type] || log.type}
                    </span>
                  </td>
                  <td className="px-[18px] py-[10px] text-[#374151] text-[13px]">{(log.to || []).join(', ')}</td>
                  <td className="px-[18px] py-[10px] text-[#374151] text-[13px]">
                    {log.subject}{log.attachmentUrl && <span className="ml-1.5 text-[#9CA3AF]" title="Has an attachment">📎</span>}
                  </td>
                  <td className="px-[18px] py-[10px]">
                    {log.status === 'sent' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#065F46]">
                        <span className="w-[5px] h-[5px] rounded-full bg-[#10B981]" />
                        Sent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-dr" title={log.error || ''}>
                        <span className="w-[5px] h-[5px] rounded-full bg-dr" />
                        Failed
                      </span>
                    )}
                  </td>
                  <td className="px-[18px] py-[10px] text-right">
                    <button
                      onClick={() => setPreview(log)}
                      className="text-[12px] text-pb hover:text-navy font-medium transition"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
